/**
 * Mehrere Bilder werden EIN Beleg.
 *
 * Das ist die Regel aus Bauplan §15.2: Wer eine dreiseitige Rechnung
 * abfotografiert und drei Bilder schickt, bekommt sonst drei Belege mit
 * dreimal „Betrag fehlt" — und muss zwei davon von Hand ablehnen. Der Server
 * fuegt die Bilder zu einem PDF zusammen (`POST /documents` mit mehreren
 * `dateien`, Antwort `UploadErgebnis.seiten`); diese Datei sorgt dafuer, dass
 * die Oberflaeche ihm nur Mengen schickt, aus denen sich ueberhaupt ein Beleg
 * bauen laesst.
 *
 * Bewusst ohne React und ohne Browser-APIs: die Regeln sind hier testbar, die
 * `URL.createObjectURL`-Seite bleibt in der Komponente.
 */

/** Was der Server annimmt (`services/annahme.py`, Magic-Bytes-Pruefung). */
export const ERLAUBTE_TYPEN = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];

/** Grenze je Datei. Der Server prueft sie noch einmal — hier spart sie den Upload. */
export const MAX_BYTES = 25 * 1024 * 1024;

/** Mehr Seiten als das ist kein Beleg mehr, sondern ein Versehen. */
export const MAX_SEITEN = 20;

export interface Seite {
  /** Nur fuer React-Keys und zum Entfernen — nichts geht damit zum Server. */
  id: string;
  name: string;
  groesse: number;
  mimeType: string;
  datei: File;
  /** `blob:`-Adresse fuer die Miniatur; `null` bei PDF. */
  vorschauUrl: string | null;
}

export type AbweisGrund =
  | 'typ_unbekannt'
  | 'zu_gross'
  | 'pdf_neben_bildern'
  | 'zweites_pdf'
  | 'zu_viele_seiten';

export interface Abgewiesen {
  name: string;
  grund: AbweisGrund;
}

export const ABWEIS_TEXT: Record<AbweisGrund, string> = {
  typ_unbekannt: 'Nur PDF und Bilder — anderes nimmt die Buchhaltung nicht an.',
  zu_gross: 'Größer als 25 MB.',
  pdf_neben_bildern:
    'Ein PDF ist schon ein fertiger Beleg — es lässt sich nicht mit Fotos zu einem zusammenfügen. Schick es einzeln.',
  zweites_pdf: 'Pro Beleg genau ein PDF. Das zweite gehört in einen eigenen Upload.',
  zu_viele_seiten: `Mehr als ${MAX_SEITEN} Seiten sind kein Beleg mehr.`,
};

export interface Zusammenfuehrung {
  /** Der Stapel, wie er nach dem Hinzufuegen aussieht. */
  seiten: Seite[];
  /** Was nicht dazupasste — mit Grund, denn stilles Verschlucken waere schlimmer. */
  abgewiesen: Abgewiesen[];
}

function istPdf(seite: Seite): boolean {
  return seite.mimeType.toLowerCase().includes('pdf');
}

/**
 * Seiten zu einem Beleg zusammenfuehren.
 *
 * Die Regeln, in dieser Reihenfolge:
 *
 * 1. Was der Server ohnehin ablehnt (fremder Typ, zu gross), fliegt sofort raus.
 * 2. Ein PDF ist ein fertiger Beleg. Es steht allein — weder neben Fotos noch
 *    neben einem zweiten PDF.
 * 3. Bilder sammeln sich, bis `MAX_SEITEN` erreicht ist.
 *
 * Der Stapel bleibt in der Reihenfolge, in der er entstanden ist: bei einer
 * mehrseitigen Rechnung ist die Reihenfolge der Seiten Teil des Belegs.
 */
export function seitenHinzufuegen(bestand: Seite[], neue: Seite[]): Zusammenfuehrung {
  const seiten = [...bestand];
  const abgewiesen: Abgewiesen[] = [];

  for (const seite of neue) {
    if (!ERLAUBTE_TYPEN.includes(seite.mimeType.toLowerCase())) {
      abgewiesen.push({ name: seite.name, grund: 'typ_unbekannt' });
      continue;
    }
    if (seite.groesse > MAX_BYTES) {
      abgewiesen.push({ name: seite.name, grund: 'zu_gross' });
      continue;
    }

    const bestandHatPdf = seiten.some(istPdf);

    if (istPdf(seite)) {
      if (bestandHatPdf) {
        abgewiesen.push({ name: seite.name, grund: 'zweites_pdf' });
        continue;
      }
      if (seiten.length > 0) {
        abgewiesen.push({ name: seite.name, grund: 'pdf_neben_bildern' });
        continue;
      }
    } else if (bestandHatPdf) {
      abgewiesen.push({ name: seite.name, grund: 'pdf_neben_bildern' });
      continue;
    }

    if (seiten.length >= MAX_SEITEN) {
      abgewiesen.push({ name: seite.name, grund: 'zu_viele_seiten' });
      continue;
    }

    seiten.push(seite);
  }

  return { seiten, abgewiesen };
}

/** Eine Seite wieder herausnehmen. */
export function seiteEntfernen(bestand: Seite[], id: string): Seite[] {
  return bestand.filter((seite) => seite.id !== id);
}

/**
 * Eine Seite verschieben — die Reihenfolge landet so im zusammengefuegten PDF.
 *
 * Ausserhalb des Stapels wird nichts verschoben: ein Klick auf „hoch" bei der
 * ersten Seite darf die Liste nicht umsortieren.
 */
export function seiteVerschieben(bestand: Seite[], id: string, richtung: -1 | 1): Seite[] {
  const von = bestand.findIndex((seite) => seite.id === id);
  if (von === -1) return bestand;
  const nach = von + richtung;
  if (nach < 0 || nach >= bestand.length) return bestand;

  const neu = [...bestand];
  const [seite] = neu.splice(von, 1);
  neu.splice(nach, 0, seite);
  return neu;
}

/** „3 Seiten — wird ein Beleg" bzw. „1 Seite". */
export function stapelText(anzahl: number): string {
  if (anzahl === 0) return 'Noch nichts ausgewählt';
  if (anzahl === 1) return '1 Seite — wird ein Beleg';
  return `${anzahl} Seiten — werden ein Beleg`;
}
